import React, { useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import { ResearchInterest } from 'js/liswp/types';

import ResearchInterestPopover from './popovers/research_interest_popover';

const INITIAL_RI_LIMIT = 5;

type ResearchInterestNameProps = {
  interest: ResearchInterest;
  fetchResearchInterestFollowerCount: (interest: { id: number }) => void;
  followResearchInterest: (
    interest: { id: number },
    callback: () => void,
  ) => void;
  unfollowResearchInterest: (
    interest: { id: number },
    callback: () => void,
  ) => void;
  comma: boolean;
  addClickTracking: (url: string) => string;
  enablePopover: boolean;
  loggedIn: boolean;
};

const ResearchInterestName: React.FC<ResearchInterestNameProps> = ({
  interest,
  comma,
  addClickTracking,
  enablePopover,
  fetchResearchInterestFollowerCount,
  followResearchInterest,
  unfollowResearchInterest,
  loggedIn,
}) => {
  const [showPopover, setShowPopover] = useState<boolean>(false);

  const handleShowPopover = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPopover(true);
  };

  const handleHidePopover = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowPopover(false);
  };

  return (
    <div
      className="work-card--research-interest"
      onMouseEnter={enablePopover ? handleShowPopover : undefined}
      onMouseLeave={enablePopover ? handleHidePopover : undefined}
    >
      <a href={addClickTracking(interest.url)} className="work-card--link">
        <div>
          {interest.name}
          {comma && ', '}
        </div>
      </a>
      {enablePopover && (
        <div className="work-card--ri-popover-outer-wrapper">
          <CSSTransition
            in={showPopover}
            classNames="work-card--ri-popover"
            timeout={{ appear: 200, enter: 200, exit: 200 }}
            unmountOnExit
          >
            <ResearchInterestPopover
              interest={interest}
              fetchResearchInterestFollowerCount={
                fetchResearchInterestFollowerCount
              }
              followResearchInterest={followResearchInterest}
              unfollowResearchInterest={unfollowResearchInterest}
              addClickTracking={addClickTracking}
              loggedIn={loggedIn}
            />
          </CSSTransition>
        </div>
      )}
    </div>
  );
};

const intersperse = (arr: any) => {
  const sep = (n: any) => (
    <span className="work-card--space" key={`space-${n}`}>
      {' '}
    </span>
  );
  return arr
    .reduce((acc: any, val: any) => [...acc, val, sep(acc.length)], [])
    .slice(0, -1);
};

type ResearchInterestListProps = {
  interests: ResearchInterest[];
  fetchResearchInterestFollowerCount?: (interest: { id: number }) => void;
  followResearchInterest?: (
    interest: { id: number },
    callback: () => void,
  ) => void;
  unfollowResearchInterest?: (
    interest: { id: number },
    callback: () => void,
  ) => void;
  addClickTracking?: (url: string) => string;
  enablePopover?: boolean;
  loggedIn?: boolean;
  showSearchRedesign?: boolean;
};

/* Renders a list of ResearchInterestNames, with a limit of 5 and wrapping enabled.
 * Each RI name will render a RI popover on hover.
 */
const ResearchInterestList: React.FC<ResearchInterestListProps> = ({
  interests,
  fetchResearchInterestFollowerCount = () => {},
  followResearchInterest = () => {},
  unfollowResearchInterest = () => {},
  addClickTracking = (url) => url,
  enablePopover = true,
  loggedIn = true,
  showSearchRedesign = false,
}) => {
  const [expanded, setExpanded] = useState(false);

  const displayedInterests = expanded
    ? interests
    : interests.slice(0, INITIAL_RI_LIMIT);

  if (displayedInterests.length === 0) {
    return null;
  }

  const showMoreButton = !expanded && interests.length > INITIAL_RI_LIMIT;
  const showLessButton = expanded && interests.length > INITIAL_RI_LIMIT;

  return (
    <div className="work-card--research-interest-outer-wrapper">
      {!showSearchRedesign && <i className="fa fa-tag work-card--icon-ri" />}
      <div
        className={`work-card--research-interest-list ${
          showSearchRedesign && 'redesign'
        }`}
      >
        {intersperse(
          interests.map((ri, index) => {
            return (
              <ResearchInterestName
                key={ri.id}
                interest={ri}
                comma={index !== interests.length - 1}
                fetchResearchInterestFollowerCount={
                  fetchResearchInterestFollowerCount
                }
                followResearchInterest={followResearchInterest}
                unfollowResearchInterest={unfollowResearchInterest}
                addClickTracking={addClickTracking}
                enablePopover={enablePopover}
                loggedIn={loggedIn}
              />
            );
          }),
        )}
        {showMoreButton && (
          <div
            onClick={() => {
              setExpanded(true);
            }}
            className="work-card--research-interest-more-less"
          >
            &nbsp;...more&nbsp;▾
          </div>
        )}
        {showLessButton && (
          <div
            onClick={() => {
              setExpanded(false);
            }}
            className="work-card--research-interest-more-less"
          >
            &nbsp;less&nbsp;▴
          </div>
        )}
      </div>
    </div>
  );
};

export default ResearchInterestList;
